"use client";

// "Our amenities" as two rows of icon cards that glide in opposite directions
// (a seamless marquee), pausing on hover. The list is duplicated so a
// translateX(-50%) loop has no seam; edges fade via a mask.
// When `c` is supplied, each icon + label is CMS-editable in place
// (keys: <prefix>.<i>.icon / .name); duplicated loop copies share the key.
import { CmsText } from "@/components/CmsText";

type Amenity = { name: string; icon?: string };

function Row({
  items,
  reverse,
  startIndex,
  c,
  keyPrefix,
}: {
  items: Amenity[];
  reverse?: boolean;
  startIndex: number;
  c: Record<string, string>;
  keyPrefix: string;
}) {
  const loop = [...items, ...items];
  return (
    <div
      className="flex w-max gap-4 group-hover:[animation-play-state:paused] motion-reduce:[animation:none] md:gap-5"
      style={{
        animation: `${reverse ? "ts-marquee-rev" : "ts-marquee"} 50s linear infinite`,
      }}
    >
      {loop.map((a, i) => {
        // stable per-amenity index (loop duplicates + row offset)
        const gi = startIndex + (i % items.length);
        const iconKey = `${keyPrefix}.${gi}.icon`;
        const icon = c[iconKey]?.trim() || a.icon;
        return (
          <div
            key={`${a.name}-${i}`}
            className="flex w-40 shrink-0 flex-col items-center justify-center gap-4 rounded-2xl border border-brand-line bg-brand-surface px-4 py-7 text-center transition-colors hover:border-brand-accent md:w-48"
          >
            {icon ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={icon}
                alt=""
                aria-hidden
                data-cms-img={iconKey}
                width={48}
                height={48}
                className="h-12 w-12 object-contain"
              />
            ) : null}
            <CmsText c={c} k={`${keyPrefix}.${gi}.name`} className="text-sm font-medium leading-snug text-brand-ink">
              {a.name}
            </CmsText>
          </div>
        );
      })}
    </div>
  );
}

export function AmenitiesMarquee({
  items,
  c = {},
  keyPrefix = "amenity",
}: {
  items: Amenity[];
  c?: Record<string, string>;
  keyPrefix?: string;
}) {
  if (!items || items.length === 0) return null;
  const half = Math.ceil(items.length / 2);
  const rowA = items.slice(0, half);
  const hasB = items.slice(half).length >= 2;
  const rowB = hasB ? items.slice(half) : items;

  return (
    <div className="group flex flex-col gap-4 overflow-hidden md:gap-5 [mask-image:linear-gradient(to_right,transparent,black_4%,black_96%,transparent)]">
      <Row items={rowA} startIndex={0} c={c} keyPrefix={keyPrefix} />
      <Row items={rowB} reverse startIndex={hasB ? half : 0} c={c} keyPrefix={keyPrefix} />
    </div>
  );
}
